/**
 * /l/:slug — معالج الروابط القصيرة.
 * - يقرأ slug → يفحص disabled / expires / max_clicks
 * - يحقن UTM إن لزم
 * - يسجّل النقرة قبل التحويل بعملية DB ذرّية واحدة
 * - يكتشف device/os/browser من User-Agent
 * - rate limit بسيط في الذاكرة (نعمل على مستوى الـ Worker)
 */
import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { resolveShortLinkPublic } from "@/utils/shortLinks.functions";

const SLUG_RE = /^[a-zA-Z0-9]{6,8}$/;

// rate limit بسيط: كاش في الذاكرة (إعادة الضبط مع كلّ Worker جديد)
const rateMap = new Map<string, { count: number; resetAt: number }>();
function checkRate(ipHash: string, perMin: number): boolean {
  const now = Date.now();
  const entry = rateMap.get(ipHash);
  if (!entry || entry.resetAt < now) {
    rateMap.set(ipHash, { count: 1, resetAt: now + 60_000 });
    return true;
  }
  entry.count++;
  return entry.count <= perMin;
}

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function detectDevice(ua: string): { device: string; os: string; browser: string } {
  const u = ua.toLowerCase();
  let device = "desktop";
  if (/ipad|tablet/.test(u)) device = "tablet";
  else if (/mobile|android|iphone|ipod/.test(u)) device = "mobile";

  let os = "unknown";
  if (/windows nt/.test(u)) os = "Windows";
  else if (/mac os x/.test(u)) os = "macOS";
  else if (/iphone|ipad|ipod/.test(u)) os = "iOS";
  else if (/android/.test(u)) os = "Android";
  else if (/linux/.test(u)) os = "Linux";

  let browser = "unknown";
  if (/edg\//.test(u)) browser = "Edge";
  else if (/chrome\//.test(u) && !/edg\//.test(u)) browser = "Chrome";
  else if (/safari\//.test(u) && !/chrome/.test(u)) browser = "Safari";
  else if (/firefox\//.test(u)) browser = "Firefox";
  else if (/whatsapp/.test(u)) browser = "WhatsApp";

  return { device, os, browser };
}

function injectUtm(target: string, utm: { source?: string | null; medium?: string | null; campaign?: string | null }, origin: string): string {
  try {
    const url = new URL(target, origin);
    if (utm.source && !url.searchParams.has("utm_source")) url.searchParams.set("utm_source", utm.source);
    if (utm.medium && !url.searchParams.has("utm_medium")) url.searchParams.set("utm_medium", utm.medium);
    if (utm.campaign && !url.searchParams.has("utm_campaign")) url.searchParams.set("utm_campaign", utm.campaign);
    return url.toString();
  } catch {
    return target;
  }
}

function renderBlockedPage(title: string, body: string, reason?: string): string {
  const isMaxClicks = reason === "max_clicks";
  const supportUrl = `https://wa.me/971508877701?text=${encodeURIComponent("مرحباً فريق Lecmo، واجهتُ مشكلة مع رابط وأحتاج دعماً." + (isMaxClicks ? " لقد وصل الرابط إلى الحدّ الأقصى من النقرات." : ""))}`;
  return `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${title} — Lecmo</title>
  <style>
    body { margin:0; display:flex; align-items:center; justify-content:center; min-height:100vh; font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif; background:#0a0a0a; color:#fff; text-align:center; padding:2rem; }
    .box { max-width:420px; }
    h1 { font-size:1.25rem; margin-bottom:0.75rem; font-weight:600; color:#fff; }
    p { color:#a0a0a0; font-size:0.95rem; line-height:1.7; margin-bottom:1.5rem; }
    .actions { display:flex; flex-direction:column; gap:0.75rem; align-items:center; }
    a.btn { display:inline-block; padding:0.65rem 1.25rem; border-radius:4px; font-size:0.9rem; text-decoration:none; transition:opacity 0.2s; }
    a.btn-primary { background:#c9a86c; color:#0a0a0a; }
    a.btn-primary:hover { opacity:0.9; }
    a.btn-ghost { background:transparent; border:1px solid #555; color:#a0a0a0; }
    a.btn-ghost:hover { border-color:#c9a86c; color:#c9a86c; }
  </style>
</head>
<body>
  <div class="box">
    <h1>${title}</h1>
    <p>${body}</p>
    <div class="actions">
      <a class="btn btn-primary" href="/">العودة للموقع</a>
      <a class="btn btn-ghost" href="${supportUrl}" target="_blank" rel="noopener">تواصل مع الدعم</a>
    </div>
  </div>
</body>
</html>`;
}

function logBlockAsync(args: {
  slug: string;
  reason: string;
  request: Request;
  ipHash?: string | null;
}) {
  // fire-and-forget — لا تعطّل الردّ على المستخدم
  (async () => {
    try {
      const ua = args.request.headers.get("user-agent") || "";
      const country = args.request.headers.get("cf-ipcountry") || null;
      const referer = args.request.headers.get("referer") || null;
      await supabaseAdmin.from("short_link_block_events").insert({
        slug: args.slug,
        reason: args.reason,
        ip_hash: args.ipHash ?? null,
        user_agent: ua.slice(0, 500),
        referer: referer?.slice(0, 500) ?? null,
        country,
        source: "ssr",
      });
    } catch (err) {
      console.error("[shortLinks] block log failed:", err);
    }
  })();
}

export const Route = createFileRoute("/l/$slug")({
  server: {
    handlers: {
      GET: async ({ params, request }) => {
        const slug = params.slug;
        if (!SLUG_RE.test(slug)) {
          logBlockAsync({ slug: slug.slice(0, 64), reason: "invalid_slug", request });
          return new Response(renderBlockedPage("الرابط غير صالح", "الرابط الذي تحاول الوصول إليه غير موجود أو تنسيقه غير صحيح."), { status: 200, headers: { "Content-Type": "text/html; charset=utf-8" } });
        }

        // اقرأ الإعدادات + الرابط معاً
        const [linkRes, rateSettingRes] = await Promise.all([
          supabaseAdmin
            .from("short_links")
            .select("slug, target_url, is_external, purpose, disabled_at, expires_at, utm_source, utm_medium, utm_campaign, click_count, max_clicks")
            .eq("slug", slug)
            .maybeSingle(),
          supabaseAdmin
            .from("app_settings")
            .select("value")
            .eq("key", "short_link_rate_limit_per_min")
            .maybeSingle(),
        ]);

        const link = linkRes.data;
        if (!link) {
          logBlockAsync({ slug, reason: "not_found", request });
          return new Response(renderBlockedPage("الرابط غير موجود", "الرابط الذي تحاول الوصول إليه غير مسجّل في نظامنا."), { status: 200, headers: { "Content-Type": "text/html; charset=utf-8" } });
        }
        if (link.disabled_at) {
          logBlockAsync({ slug, reason: "disabled", request });
          return new Response(renderBlockedPage("الرابط معطّل", "تمّ تعطيل هذا الرابط من قبل الإدارة. إذا كنت تعتقد أنّ هذا خطأ، تواصل مع فريق Lecmo."), { status: 200, headers: { "Content-Type": "text/html; charset=utf-8" } });
        }
        if (link.expires_at && new Date(link.expires_at) < new Date()) {
          logBlockAsync({ slug, reason: "expired", request });
          return new Response(renderBlockedPage("انتهت صلاحية الرابط", "لقد انتهت مدّة صلاحية هذا الرابط. يمكنك طلب تمديدها من الإدارة إذا لزم."), { status: 200, headers: { "Content-Type": "text/html; charset=utf-8" } });
        }
        if (link.max_clicks != null && (link.click_count ?? 0) >= link.max_clicks) {
          logBlockAsync({ slug, reason: "max_clicks", request });
          return new Response(renderBlockedPage("تمّ استنفاد الرابط", "لقد وصل هذا الرابط إلى الحدّ الأقصى من النقرات المسموح بها. يمكنك طلب تمديده من الإدارة.", "max_clicks"), { status: 200, headers: { "Content-Type": "text/html; charset=utf-8" } });
        }


        const perMin = Number(rateSettingRes.data?.value ?? 120) || 120;

        // جلب IP / Country من Cloudflare headers (متاحة في Workers على dom Cloudflare)
        const ip = request.headers.get("cf-connecting-ip")
          || request.headers.get("x-forwarded-for")?.split(",")[0]?.trim()
          || "unknown";
        const country: string | null = request.headers.get("cf-ipcountry") || null;
        const city: string | null = request.headers.get("cf-ipcity") || null;
        const ua = request.headers.get("user-agent") || "";
        const referer = request.headers.get("referer") || null;

        const ipHash = await sha256Hex(`${ip}::${slug}`);

        // rate limit
        if (!checkRate(ipHash, perMin)) {
          return new Response("Too many requests", { status: 429 });
        }

        // الوجهة النهائيّة:
        //  - الروابط الخارجيّة (منصّات: بوتيكات/أمازون/نون...) → target_url مطلق + UTM موجود فعلاً
        //  - الروابط الداخليّة → نمرّر target_url عبر injectUtm كما هو (يحلّ المسار النسبيّ على origin الحاليّ)
        const reqOrigin = new URL(request.url).origin;
        const isExternalRedirect = link.is_external === true && link.purpose === "external_platform";
        const finalUrl = isExternalRedirect
          ? link.target_url
          : injectUtm(link.target_url, {
              source: link.utm_source,
              medium: link.utm_medium,
              campaign: link.utm_campaign,
            }, reqOrigin);

        // ⚠️ تسجيل النقرة قبل الـ redirect عبر RPC ذرّي في قاعدة البيانات.
        // السبب: فصل insert/update داخل الـ Worker ترك مجالاً لفشل صامت أو عدّادات غير متزامنة.
        // هنا صفّ النقرة + click_count + unique_click_count تُحدَّث في معاملة واحدة.
        const { device, os, browser } = detectDevice(ua);
        try {
          const { error } = await supabaseAdmin.rpc("record_short_link_click", {
            _slug: slug,
            _ip_hash: ipHash,
            _user_agent: ua,
            _device_type: device,
            _os: os,
            _browser: browser,
            _referer: referer ?? undefined,
            _country: country ?? undefined,
            _city: city ?? undefined,
          });
          if (error) console.error(`[shortLinks] record click failed slug=${slug}:`, JSON.stringify(error));
        } catch (err) {
          console.error(`[shortLinks] click logging threw slug=${slug}:`, err instanceof Error ? err.message : String(err));
        }

        return new Response(null, {
          status: 302,
          headers: {
            Location: finalUrl,
            "Cache-Control": "no-store",
          },
        });
      },
    },
  },
  // Client-side fallback: لو الـ custom domain خدم HTML shell بدل تنفيذ
  // server handler أعلاه، الـ component يستدعي server fn ويحوّل من المتصفّح.
  component: ShortLinkRedirect,
});

function ShortLinkRedirect() {
  const { slug } = Route.useParams();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    resolveShortLinkPublic({ data: { slug } })
      .then((res) => {
        if (cancelled) return;
        if (res?.url) {
          window.location.replace(res.url);
        } else {
          const reason = (res as { reason?: string } | undefined)?.reason;
          setError(reason ?? "not_found");
        }
      })
      .catch((e) => {
        console.error("[ShortLinkRedirect]", e);
        if (!cancelled) setError("error");
      });
    return () => { cancelled = true; };
  }, [slug]);

  const messages: Record<string, { title: string; body: string }> = {
    not_found: { title: "الرابط غير موجود", body: "الرابط الذي تحاول الوصول إليه غير مسجّل في نظامنا." },
    disabled: { title: "الرابط معطّل", body: "تمّ تعطيل هذا الرابط من قبل الإدارة. إذا كنت تعتقد أنّ هذا خطأ، تواصل مع فريق Lecmo." },
    expired: { title: "انتهت صلاحية الرابط", body: "لقد انتهت مدّة صلاحية هذا الرابط. يمكنك طلب تمديدها من الإدارة إذا لزم." },
    max_clicks: { title: "تمّ استنفاد الرابط", body: "لقد وصل هذا الرابط إلى الحدّ الأقصى من النقرات المسموح بها. يمكنك طلب تمديده من الإدارة." },
    error: { title: "تعذّر فتح الرابط", body: "حدث خطأ تقنيّ أثناء محاولة فتح هذا الرابط. حاول مرّة أخرى لاحقاً." },
  };

  const msg = error ? messages[error] ?? messages.not_found : null;

  const isMaxClicks = error === "max_clicks";
  const supportUrl = `https://wa.me/971508877701?text=${encodeURIComponent("مرحباً فريق Lecmo، واجهتُ مشكلة مع رابط وأحتاج دعماً." + (isMaxClicks ? " لقد وصل الرابط إلى الحدّ الأقصى من النقرات." : ""))}`;

  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", textAlign: "center", direction: "rtl", fontFamily: "system-ui, sans-serif" }}>
      <div>
        {error && msg ? (
          <>
            <p style={{ fontSize: "1.25rem", marginBottom: "0.75rem", fontWeight: 600 }}>{msg.title}</p>
            <p style={{ fontSize: "0.95rem", color: "#6b6b6b", lineHeight: 1.7, marginBottom: "1.5rem" }}>{msg.body}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", alignItems: "center" }}>
              <a href="/" style={{ display: "inline-block", padding: "0.65rem 1.25rem", borderRadius: 4, fontSize: "0.9rem", textDecoration: "none", background: "#c9a86c", color: "#0a0a0a" }}>العودة للموقع</a>
              <a href={supportUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "0.65rem 1.25rem", borderRadius: 4, fontSize: "0.9rem", textDecoration: "none", background: "transparent", border: "1px solid #555", color: "#a0a0a0" }}>تواصل مع الدعم</a>
            </div>
          </>
        ) : (
          <p style={{ fontSize: "1rem", color: "#6b6b6b" }}>جاري التحويل…</p>
        )}
      </div>
    </div>
  );
}
